import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import Fuse from "fuse.js";
import React, { useEffect, useMemo, useState } from "react";
import { clx } from "../../utilities/clx.ts";
import { FakeCheckbox } from "../Checkbox/FakeCheckbox/FakeCheckbox.tsx";
import styles from "./Dropdown.module.css";
import { DropdownArrow } from "./DropdownArrow.tsx";

type DropdownItem = { label: string; value: string; details?: string };

interface DropdownProps {
  items: DropdownItem[];
  selected?: string[];
  placeholder?: string;
  onSelect: (value: DropdownItem["value"][]) => void;
  multiple?: boolean;
  minWidth?: string;
  id?: string;
  searchable?: boolean;
  sorting?: string;
}

/**
 * Function to sort an array of DropdownItem objects alphabetically by their label property.
 *
 * @returns Returns -1 if the label of 'a' comes before the label of 'b' alphabetically,
 * 1 if the label of 'a' comes after the label of 'b', and 0 if both labels are equal.
 */
const alphabeticalSort = (a: DropdownItem, b: DropdownItem) =>
  a.label < b.label ? -1 : a.label > b.label ? 1 : 0;

/**
 * A higher-order function that creates a comparator for sorting `DropdownItem` objects based on their presence
 * in the `currentValues` array. Items included in the `currentValues` array are given priority in the sort order.
 *
 * @returns A comparator function that compares two `DropdownItem` objects (`a` and `b`).
 *                     The comparator ensures that items present in `currentValues` appear earlier in the list.
 *                     If both or neither values are in `currentValues`, their relative order remains unchanged.
 */
const selectedFirst =
  (currentValues: string[]) => (a: DropdownItem, b: DropdownItem) =>
    currentValues.includes(a.value) && !currentValues.includes(b.value)
      ? -1
      : !currentValues.includes(a.value) && currentValues.includes(b.value)
        ? 1
        : 0;

const mergeDropdownItems = (
  items1: DropdownItem[],
  items2: DropdownItem[],
): DropdownItem[] =>
  Array.from(
    new Map([
      ...new Map(items1.map((item) => [item.value, item])),
      ...new Map(items2.map((item) => [item.value, item])),
    ]).values(),
  );

export const Dropdown: React.FC<DropdownProps> = ({
  items,
  selected,
  onSelect,
  placeholder,
  multiple = false,
  minWidth = "360px",
  id,
  searchable = false,
  sorting,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [currentValues, setCurrentValues] = useState<string[]>(selected || []);
  const [searchValue, setSearchValue] = useState<string>("");

  // Custom handler to clear search when dropdown closes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchValue("");
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: handleOpenChange,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({
        fallbackStrategy: "bestFit",
      }),
      size({
        apply({ availableHeight, elements, rects }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight - 32}px`,
            width: `${rects.reference.width - 2}px`,
          });
        },
      }),
    ],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
  ]);

  const fuse = useMemo(
    () =>
      new Fuse(items, {
        keys: ["label"],
        threshold: 0.3,
      }),
    [items],
  );

  const filteredItems = useMemo(() => {
    let _items = [...items];
    if (searchable && searchValue) {
      const searchResult = fuse
        .search(searchValue)
        .map((resultItem) => resultItem.item);
      const currentItems = _items.filter((item) =>
        currentValues.includes(item.value),
      );
      _items = mergeDropdownItems(currentItems, searchResult);
    }

    if (sorting === "alphabetical" && !searchValue) {
      _items.sort(alphabeticalSort);
    }

    return _items.sort(selectedFirst(currentValues));
  }, [currentValues, fuse, items, searchValue, searchable, sorting]);

  const currentItems: DropdownItem[] = items.filter((item) =>
    currentValues.includes(item.value),
  );

  const handleSelect = (item: DropdownItem) => {
    if (multiple) {
      const isRemoving: boolean = currentValues.includes(item.value);
      const newValues = isRemoving
        ? currentValues.filter((value) => value !== item.value)
        : [...currentValues, item.value];
      setCurrentValues(newValues);
      onSelect(newValues);
      return;
    }
    setCurrentValues([item.value]);
    setOpen(false);
    onSelect([item.value]);
  };

  useEffect(() => {
    setCurrentValues(selected || []);
  }, [selected]);

  return (
    <div className={`${styles.dropdownContainer}`}>
      <button
        ref={refs.setReference}
        type="button"
        className={`${styles.dropdownButton} ${!currentValues && styles.dropdownPlaceholder}`}
        style={{ minWidth: minWidth }}
        id={id}
        {...getReferenceProps()}
      >
        {currentItems.map((item) => item.label).join(", ") || placeholder}{" "}
        <DropdownArrow rotated={open} />
      </button>
      <>
        {open && (
          <FloatingPortal>
            <ul
              ref={refs.setFloating}
              style={floatingStyles}
              className={`${styles.dropdownList}`}
              {...getFloatingProps()}
            >
              {searchable && (
                <div className={`${styles.dropdownSearchContainer}`}>
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search..."
                    className={`${styles.dropdownSearchInput}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              {filteredItems.map((item) => (
                <li
                  key={item.value}
                  onClick={() => handleSelect(item)}
                  className={clx([
                    styles.dropdownListItem,
                    currentValues.includes(item.value) &&
                      styles.dropdownListItemSelected,
                  ])}
                >
                  {multiple && (
                    <FakeCheckbox
                      checked={currentValues.includes(item.value)}
                    />
                  )}
                  <span
                    className={clx([
                      item.details && styles.dropdownLabelWithDetails,
                    ])}
                  >
                    {item.label}
                  </span>
                  {item.details && (
                    <div className={styles.dropdownItemDetails}>
                      <span>{item.details}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </FloatingPortal>
        )}
      </>
    </div>
  );
};
